import {
  delay,
  take,
  call,
  fork,
  select,
  all,
  put,
  takeLatest,
  takeEvery,
  cancel,
  race
} from "redux-saga/effects";
import { Types, setPhoneRecordingStatus } from "actions/phone";
import { formatDate } from "utils/dates";
import { message, errorMessage } from "actions/message";
import { listenCallEvents } from "./event-handler";
import { selectors } from "reducers";
import * as phoneApi from "utils/phone-api";
import { setDialerCallPromptStatus } from "actions/auto-dialer";
/* eslint-disable no-console */

// Automated voicemail can only target an outbound leg that was dialed to a real
// phone number (the value stamped into Redux via SET_DIALED_NUMBER at connect
// time). Queue-answered and inbound legs never set a dialed number, so it comes
// back empty. Mirrors the PhoneSystem backend's IsValidVoicemailPhoneNumber guard
// (rejects empty, JS sentinels, and anything with fewer than 7 digits) so we can
// short-circuit with a clear message instead of a server-side error.
export function isDialableNumber(value) {
  if (!value) return false;
  const trimmed = String(value).trim();
  if (["undefined", "null", "nan"].includes(trimmed.toLowerCase())) return false;
  const digitCount = (trimmed.match(/\d/g) || []).length;
  return digitCount >= 7;
}

function* watchHangup(_, device) {
  try {
    while (true) {
      yield take(Types.HANGUP);
      device.disconnectAll();
    }
  } catch (e) {
    console.log(e);
  }
}

function* watchMute(twilioCall) {
  while (true) {
    yield take(Types.TOGGLE_MUTE);
    const isMuted = twilioCall.isMuted();
    twilioCall.mute(!isMuted);
  }
}

function* watchAccept(twilioCall) {
  while (true) {
    yield take(Types.ACCEPT_INCOMING);
    twilioCall.accept();
  }
}

function* watchReject(twilioCall) {
  while (true) {
    yield take(Types.REJECT_INCOMING);
    twilioCall.reject();
  }
}

function* watchPauseRecording() {
  yield takeLatest(
    Types.PAUSE_PHONE_RECORDING,
    updatePhoneRecordingStatus,
    true
  );
}

function* watchResumeRecording() {
  yield takeLatest(
    Types.RESUME_PHONE_RECORDING,
    updatePhoneRecordingStatus,
    false
  );
}

function* updatePhoneRecordingStatus(pause) {
  try {
    const callDirection = yield select(selectors.getCallDirection);
    const callSid = yield select(selectors.getTwilioCallSid);
    const phoneApiUrl = yield select(selectors.getPhoneApiUrl);
    const response = yield call(phoneApi.setRecordingPause, phoneApiUrl, {
      callDirection,
      callSid,
      paused: pause
    });
    // PhoneSystem returns { callSid, paused, inConference }; Back_End returns { status }
    const status =
      response && typeof response === "object" && "status" in response
        ? response.status
        : pause
          ? "paused"
          : "in-progress";
    yield put(setPhoneRecordingStatus(status));
  } catch (e) {
    console.error(e);
  }
}

export function* watchCallActions(twilioCall, device) {
  yield all([
    call(watchHangup, twilioCall, device),
    call(watchMute, twilioCall),
    call(watchAccept, twilioCall),
    call(watchReject, twilioCall),
    call(watchPauseRecording, twilioCall),
    call(watchResumeRecording, twilioCall),
    call(watchAutomatedVoicemailRequest),
    call(watchDigitInputTask, twilioCall)
  ]);
}

function* watchAutomatedVoicemailRequest() {
  while (true) {
    try {
      const {
        payload: { patientGuid }
      } = yield take(Types.PLAY_AUTOMATED_VOICEMAIL);
      // Source the dialed number from Redux, NOT twilioCall.customParameters.
      // For outgoing calls the SDK's customParameters Map is empty (it only carries
      // TwiML <Parameter> values on inbound legs), so get("To") returns undefined and
      // was being serialized into the request URL as the literal "undefined" — the
      // cause of the /voicemail/undefined/{guid} 400. watchCall stamps the real
      // number via SET_DIALED_NUMBER at connect time (sagas/phone/index.js), and it
      // tracks whichever leg (primary or secondary) is currently active.
      const dialedNumber = yield select(selectors.getDialedNumber);

      // Guard non-dialable legs (queue/inbound carry no dialed number) so the user
      // gets a clear message instead of the backend's "Invalid phone number for
      // voicemail transfer."
      if (!isDialableNumber(dialedNumber)) {
        yield put(
          errorMessage("Automated voicemail isn't available for this call.")
        );
        continue;
      }

      const phoneApiUrl = yield select(selectors.getPhoneApiUrl);
      const response = yield call(
        phoneApi.sendAutomatedVoicemail,
        phoneApiUrl,
        { dialedNumber, patientGuid }
      );

      // The two backends don't return the same shape, and neither guarantees the
      // outreach fields:
      //   - PhoneSystem  POST /twilio/transfer/voicemail/{number}/{guid}
      //       { message, outreachDate, outreachType } — but outreachDate is null
      //       when no outreach push happened (no cached patient context, or the
      //       outreach date was already in the future), and outreachType is
      //       nullable alongside it.
      //   - Back_End     POST /twilio/new/transfer/RedirectCallIntoVoicemailSay/...
      //       { message } only — it never returned the outreach fields at all.
      // Blindly interpolating produced "…, changed outreach to undefined" on the
      // legacy path. Only append the outreach clause when we actually have both.
      const { message: status, outreachDate, outreachType } = response || {};
      const formattedOutreachDate = formatDate(outreachDate, "L");
      const outreachSummary =
        outreachType && formattedOutreachDate
          ? `, changed outreach to ${outreachType} ${formattedOutreachDate}`
          : "";
      yield put(message(`${status || "Leaving a voicemail"}${outreachSummary}`));
      yield put({ type: Types.PLAY_AUTOMATED_VOICEMAIL_SUCCESS });
      yield put({ type: Types.HANGUP });
    } catch (error) {
      // Keep the technical detail for debugging (New Relic Browser captures it),
      // but show the end user a clean message rather than the raw error/JSON.
      console.error("Automated voicemail failed:", error);
      yield put(
        errorMessage(
          "Unable to leave an automated voicemail for this call. Please try again, or leave a message manually."
        )
      );
    }
  }
}

export function* handleCallConnection(device, action) {
  try {
    const { twilioCall } = action.payload;
    const watchCallActionsTask = yield fork(
      watchCallActions,
      twilioCall,
      device
    );
    const callTask = yield fork(listenCallEvents, twilioCall, device);
    const durationTask = yield fork(updateCallDuration);

    //Ask for call disposition here
    const { leftMessage } = yield race({
      leftMessage: take(Types.PLAY_AUTOMATED_VOICEMAIL_SUCCESS),
      ended: take(Types.TWILIO_EVENT_DISCONNECT)
    });
    //If we don't leave a message, ask for disposition
    //If we do, skip disposition
    var orderPlaced = yield select(state =>
      selectors.getAutodialerPlacedOrder(state)
    );
    if (!leftMessage && !orderPlaced)
      yield put(setDialerCallPromptStatus(true));
    else yield take(Types.TWILIO_EVENT_DISCONNECT);
    yield cancel([
      // connectionHandlerTask,
      callTask,
      durationTask,
      watchCallActionsTask
    ]);
  } catch (e) {
    console.error(e);
  }
}

function* watchDigitInputTask(twilioCall) {
  yield takeEvery(Types.SEND_DIGITS, function ({ payload: { digits } }) {
    if (/^([0-9]|\*|#)+$/.test(digits)) {
      twilioCall.sendDigits(digits);
    }
  });
}

function* updateCallDuration() {
  let seconds = 0;
  while (true) {
    yield put({
      type: Types.CALL_DURATION,
      payload: { seconds }
    });
    ++seconds;
    yield delay(1000);
  }
}

export default function* watchConnection(device) {
  yield takeLatest(Types.TWILIO_EVENT_CONNECT, handleCallConnection, device);
}
